import { api } from "@/lib/api";
import {
	BESTUHLUNG_OPTIONS,
	BOOKING_ARTS,
	BOOKING_STATUSES,
	QUELLEN,
	ZEITBLOCKS,
} from "@booking-hub/shared";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// ============================================================
// Booking Create — Neue Buchung erstellen
// ============================================================

type FormData = Record<string, string | number | boolean | null>;

const INITIAL: FormData = {
	kunde: "",
	firma: "",
	kontaktEmail: "",
	kontaktTel: "",
	strasse: "",
	plz: "",
	ort: "",
	datum: "",
	datumEnde: "",
	personen: "",
	art: "",
	zeitblock: "",
	uhrzeiten: "",
	bestuhlung: "",
	quelle: "",
	status: "Anfrage",
	preis: "",
	sonderwuensche: "",
	notizen: "",
	catering: "",
};

export function BookingCreatePage() {
	const navigate = useNavigate();
	const [form, setForm] = useState<FormData>({ ...INITIAL });

	const mutation = useMutation({
		mutationFn: (data: Record<string, unknown>) =>
			api.post<{ data: { id: string } }>("/bookings", data),
		onSuccess: (res) => {
			navigate(`/bookings/${res.data.id}`);
		},
	});

	function set(key: string, value: string | number | null) {
		setForm((prev) => ({ ...prev, [key]: value }));
	}

	function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		const payload: Record<string, unknown> = {};

		for (const [k, v] of Object.entries(form)) {
			if (v === "" || v === null) continue;
			if (k === "personen" || k === "preis") {
				const num = Number(v);
				if (!Number.isNaN(num) && num > 0) payload[k] = num;
			} else {
				payload[k] = v;
			}
		}

		if (!payload.kunde) return;
		mutation.mutate(payload);
	}

	return (
		<div className="space-y-4 animate-fade-in">
			<div className="flex items-center gap-3">
				<Link to="/bookings" className="btn-ghost p-2">
					<ArrowLeft size={18} />
				</Link>
				<h1 className="font-display text-2xl">Neue Buchung</h1>
			</div>

			<form onSubmit={handleSubmit} className="space-y-6">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* Veranstaltung */}
					<div className="card space-y-4">
						<h3 className="font-medium text-stone-700">Veranstaltung</h3>
						<Field
							label="Datum"
							value={form.datum}
							field="datum"
							type="date"
							onChange={set}
						/>
						<Field
							label="Datum Ende"
							value={form.datumEnde}
							field="datumEnde"
							type="date"
							onChange={set}
						/>
						<SelectField
							label="Art"
							value={form.art}
							field="art"
							options={BOOKING_ARTS}
							onChange={set}
						/>
						<Field
							label="Personen"
							value={form.personen}
							field="personen"
							type="number"
							onChange={set}
						/>
						<SelectField
							label="Zeitblock"
							value={form.zeitblock}
							field="zeitblock"
							options={ZEITBLOCKS}
							onChange={set}
						/>
						<Field
							label="Uhrzeiten"
							value={form.uhrzeiten}
							field="uhrzeiten"
							onChange={set}
						/>
						<SelectField
							label="Bestuhlung"
							value={form.bestuhlung}
							field="bestuhlung"
							options={BESTUHLUNG_OPTIONS}
							onChange={set}
						/>
						<SelectField
							label="Quelle"
							value={form.quelle}
							field="quelle"
							options={QUELLEN}
							onChange={set}
						/>
						<SelectField
							label="Status"
							value={form.status}
							field="status"
							options={BOOKING_STATUSES}
							onChange={set}
						/>
						<Field
							label="Preis"
							value={form.preis}
							field="preis"
							type="number"
							onChange={set}
						/>
					</div>

					{/* Kontakt */}
					<div className="card space-y-4">
						<h3 className="font-medium text-stone-700">Kontakt</h3>
						<Field
							label="Kunde *"
							value={form.kunde}
							field="kunde"
							onChange={set}
							required
						/>
						<Field
							label="Firma"
							value={form.firma}
							field="firma"
							onChange={set}
						/>
						<Field
							label="E-Mail"
							value={form.kontaktEmail}
							field="kontaktEmail"
							type="email"
							onChange={set}
						/>
						<Field
							label="Telefon"
							value={form.kontaktTel}
							field="kontaktTel"
							onChange={set}
						/>
						<Field
							label="Straße"
							value={form.strasse}
							field="strasse"
							onChange={set}
						/>
						<Field label="PLZ" value={form.plz} field="plz" onChange={set} />
						<Field label="Ort" value={form.ort} field="ort" onChange={set} />
					</div>
				</div>

				{/* Details */}
				<div className="card space-y-4">
					<h3 className="font-medium text-stone-700">Details</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<TextArea
							label="Sonderwünsche"
							value={form.sonderwuensche}
							field="sonderwuensche"
							onChange={set}
						/>
						<TextArea
							label="Notizen"
							value={form.notizen}
							field="notizen"
							onChange={set}
						/>
					</div>
					<TextArea
						label="Catering"
						value={form.catering}
						field="catering"
						onChange={set}
					/>
				</div>

				{mutation.isError && (
					<p className="text-sm text-red-600">
						{mutation.error instanceof Error
							? mutation.error.message
							: "Fehler beim Erstellen"}
					</p>
				)}

				<div className="flex justify-end gap-2">
					<Link to="/bookings" className="btn-ghost text-sm">
						Abbrechen
					</Link>
					<button
						type="submit"
						disabled={mutation.isPending || !form.kunde}
						className="btn-primary text-sm disabled:opacity-40"
					>
						{mutation.isPending ? (
							<Loader2 size={14} className="animate-spin inline mr-1" />
						) : (
							<Save size={14} className="inline mr-1" />
						)}
						Buchung erstellen
					</button>
				</div>
			</form>
		</div>
	);
}

// ============================================================
// Field Components
// ============================================================

function Field({
	label,
	value,
	field,
	type = "text",
	onChange,
	required,
}: {
	label: string;
	value: FormData[string];
	field: string;
	type?: string;
	onChange: (k: string, v: string) => void;
	required?: boolean;
}) {
	return (
		<div>
			<label
				htmlFor={`f-${field}`}
				className="text-xs font-medium text-stone-500 block mb-1"
			>
				{label}
			</label>
			<input
				id={`f-${field}`}
				type={type}
				value={String(value ?? "")}
				onChange={(e) => onChange(field, e.target.value)}
				className="input"
				required={required}
			/>
		</div>
	);
}

function SelectField({
	label,
	value,
	field,
	options,
	onChange,
}: {
	label: string;
	value: FormData[string];
	field: string;
	options: readonly string[];
	onChange: (k: string, v: string) => void;
}) {
	return (
		<div>
			<label
				htmlFor={`f-${field}`}
				className="text-xs font-medium text-stone-500 block mb-1"
			>
				{label}
			</label>
			<select
				id={`f-${field}`}
				value={String(value ?? "")}
				onChange={(e) => onChange(field, e.target.value)}
				className="input"
			>
				<option value="">—</option>
				{options.map((o) => (
					<option key={o}>{o}</option>
				))}
			</select>
		</div>
	);
}

function TextArea({
	label,
	value,
	field,
	onChange,
}: {
	label: string;
	value: FormData[string];
	field: string;
	onChange: (k: string, v: string) => void;
}) {
	return (
		<div>
			<label
				htmlFor={`f-${field}`}
				className="text-xs font-medium text-stone-500 block mb-1"
			>
				{label}
			</label>
			<textarea
				id={`f-${field}`}
				value={String(value ?? "")}
				onChange={(e) => onChange(field, e.target.value)}
				className="input min-h-[80px]"
			/>
		</div>
	);
}
